import { useEffect, useState } from 'react'
import { contractsApi, type ErmesJobAccepted } from '../api/contracts'
import { documentsApi, type DocumentReadKind } from '../api/documents'
import { jobsApi } from '../api/jobs'
import { CLIENTE_ADDRESS_KEYS, applyClientAddress, clientAddressFromDocs, portalFromDoc } from '../lib/docPortal'
import { SIGNED_PDA_DOCUMENT_TYPE, SIGNED_PDA_KEY, operatorPortalConfig, tipoClienteFromSegment } from '../lib/operatorPortalConfig'
import { dynamicProfileDefaults } from '../lib/portalProfiles'
import { coralyUploadFromCrm } from '../lib/portalUploadSteps'
import { PraticaPortalSections, missingPortalFields, portalFieldLabel, withPortalDefaults } from './PraticaPortalSections'
import { PortalUploadGuide } from './PortalUploadGuide'
import { FornitureNoteSelect } from './FornitureNoteSelect'
import { Button, Callout, Card, CardContent, CardHeader, HelpTip, LoadingState, useConfirm } from './ui'

/**
 * Dati per il portale (anagrafica, indirizzo, pagamento...) inseriti alla creazione della pratica: qui si rivedono e,
 * finché la pratica è in Bozza o Integrazione richiesta, si correggono con le stesse sezioni del form.
 */
export function ContractPortalDataCard({ contractId, operatorId, operatorName, customerSegment, readOnly, canUploadPortal, canUpdateErmes, status, portalCode, reloadKey, customerId, onSaved }: {
  contractId: number; operatorId?: number | null; operatorName: string | undefined; customerSegment: string | null | undefined
  readOnly: boolean
  /** Stato e codice portale della pratica, per la guida "Come si carica". */
  status?: string
  portalCode?: string | null
  /** Cambia dopo un "Sync portale": i dati si rileggono. */
  reloadKey?: number
  /** Cliente della pratica: per scegliere un POD/PDR già noto in "Modifica dati". */
  customerId?: number | null
  /** Staff: pulsante per caricare (o riprovare) la pratica sul portale dell'operatore. */
  canUploadPortal?: boolean
  /** Staff, bozza Ermes già creata: pulsante per ricompilarla con i dati corretti nel CRM. */
  canUpdateErmes?: boolean
  onSaved: () => void
}) {
  const [saved, setSaved] = useState<Record<string, string> | null>(null)
  const [draft, setDraft] = useState<Record<string, string> | null>(null)
  const confirm = useConfirm()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // PDA firmata già tra gli allegati: id del documento e un File segnaposto con il suo nome, per il riquadro.
  const [pda, setPda] = useState<{ id: number; file: File } | null>(null)
  const [pdaBusy, setPdaBusy] = useState(false)
  const [uploadMsg, setUploadMsg] = useState<string | null>(null)
  const [rereading, setRereading] = useState(false)

  /**
   * Rilegge carta d'identità, visura e bolletta già allegate e propone i campi ancora vuoti (non sovrascrive): si
   * controllano in "Modifica dati" e si salvano con Salva.
   */
  async function rereadDocuments() {
    setRereading(true)
    setError(null)
    setUploadMsg(null)
    try {
      const kinds: DocumentReadKind[] = ['visura', 'carta_identita', 'bolletta']
      // Prima la visura (legale rappresentante), poi la carta d'identità (nascita e documento), poi la bolletta.
      const docs = (await documentsApi.list('Contract', contractId))
        .filter(d => kinds.includes(d.documentType as DocumentReadKind))
        .sort((a, b) => kinds.indexOf(a.documentType as DocumentReadKind) - kinds.indexOf(b.documentType as DocumentReadKind))
      if (docs.length === 0) { setUploadMsg("Nessuna carta d'identità, visura o bolletta tra gli allegati."); return }
      const next = { ...(draft ?? saved ?? {}) }
      const business = tipo === 'AZIENDA' || docs.some(d => d.documentType === 'visura')
      let filled = 0
      const letti: Partial<Record<DocumentReadKind, Record<string, string>>> = {}
      for (const d of docs) {
        const kind = d.documentType as DocumentReadKind
        const r = await documentsApi.readProposal(d.id, kind)
        const values = Object.fromEntries(r.fields.map(f => [f.name, f.value]))
        letti[kind] = { ...(letti[kind] ?? {}), ...values }
        for (const [k, v] of Object.entries(portalFromDoc(kind, values, business)))
          if (!next[k]?.trim()) { next[k] = v; filled++ }
      }
      // L'indirizzo del cliente segue i documenti (visura, poi intestazione della bolletta, poi carta d'identità del
      // privato) anche sopra quello già compilato dalla scheda cliente (30/10/2026).
      const addr = clientAddressFromDocs(letti, business)
      const conIndirizzo = addr ? applyClientAddress(next, addr.fields) : next
      const indirizzoCambiato = CLIENTE_ADDRESS_KEYS.some(k => (conIndirizzo[k] ?? '') !== (next[k] ?? ''))
      const fonte = addr?.source === 'visura' ? 'sede legale della visura' : addr?.source === 'bolletta' ? 'intestazione della bolletta' : "residenza della carta d'identità"
      setDraft(conIndirizzo)
      setUploadMsg(filled > 0 || indirizzoCambiato
        ? `Letti dai documenti ${filled} campi vuoti${indirizzoCambiato ? ` e aggiornato l'indirizzo del cliente (${fonte})` : ''}: controllali e premi Salva.`
        : 'I documenti non aggiungono dati: i campi erano già compilati.')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRereading(false)
    }
  }
  const [uploading, setUploading] = useState(false)
  // Comando Ermes in corso: etichetta e secondi trascorsi (il job gira sul server, il portale risponde in 30 s - 2 min).
  const [jobRun, setJobRun] = useState<{ label: string; elapsed: number } | null>(null)

  // Dopo l'accodamento si segue il job (GET api/jobs/mine/{id}) finché finisce: avanzamento ed esito compaiono qui,
  // senza ricaricare la pagina. Alla fine si rileggono i dati portale (stato firma, errori da sistemare, allegati).
  async function seguiJob(accepted: ErmesJobAccepted, label: string) {
    setUploadMsg(accepted.message)
    if (!accepted.jobId) return
    const started = Date.now()
    setJobRun({ label, elapsed: 0 })
    try {
      for (;;) {
        await new Promise(r => setTimeout(r, 3000))
        setJobRun({ label, elapsed: Math.round((Date.now() - started) / 1000) })
        const s = await jobsApi.mine(accepted.jobId).catch(() => null)
        if (s?.finished) {
          const res = (s.result ?? {}) as { ok?: boolean; esito?: string }
          if (s.status === 'processed' && res.ok !== false) { setUploadMsg(res.esito || `${label}: fatto.`) }
          else { setUploadMsg(null); setError(res.esito || s.error || `${label}: non riuscito (vedi le note della pratica).`) }
          break
        }
        if (Date.now() - started > 8 * 60_000) { setUploadMsg(`${label}: ancora in corso sul portale, l'esito arriva nelle note della pratica.`); break }
      }
    } finally {
      setJobRun(null)
      void contractsApi.getPortalData(contractId).then(d => setSaved(d ?? {})).catch(() => { /* resta com'è */ })
    }
  }

  // Errori del portale all'ultimo invio alla firma (dati portale ERMES_ERRORI): da sistemare nel CRM, poi si rimanda.
  let ermesErrori: { campo?: string | null; messaggio: string; passo?: number | null }[] = []
  try { ermesErrori = JSON.parse(saved?.['ERMES_ERRORI'] || '[]') as typeof ermesErrori } catch { /* dato rovinato: nessun elenco */ }
  // «Modifica dati»: la bozza parte dai dati salvati più i valori proposti dal modulo (es. Sky: data inserimento brand = oggi).
  function entraInModifica() {
    if (!saved) return
    setDraft({ ...saved, ...(config.profile ? dynamicProfileDefaults(config.profile, saved) : {}) })
  }
  function correggi(campo?: string | null) {
    if (draft === null && saved) entraInModifica()
    if (!campo) return
    window.setTimeout(() => {
      const el = document.getElementById(`pp-${campo}`) ?? document.querySelector<HTMLElement>(`[id^="pp-${campo}-"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus()
    }, 150)
  }

  // Invio alla firma digitale OTP: il portale controlla i dati e, se manca qualcosa, lo dice (pannello "da sistemare").
  async function signErmes(prova = false) {
    const ok = prova
      ? await confirm({ title: `Provare l'invio alla firma della bozza ${portalCode ?? ''}?`, message: 'Ricompila la bozza, fa fare al portale i controlli sui dati e apre la pagina di firma, senza inviare nulla al cliente. Gli eventuali dati da sistemare compaiono qui sotto.', confirmLabel: 'Prova' })
      : await confirm({ title: `Inviare la bozza ${portalCode ?? ''} alla firma OTP?`, message: 'La bozza viene prima ricompilata con i dati del CRM, poi Ermes la manda al cliente per la firma digitale con codice OTP (via email se c\'è, altrimenti SMS). Dopo l\'invio la bozza non è più modificabile dal CRM. Se il portale segnala dati mancanti, compaiono qui sotto da sistemare.', confirmLabel: 'Invia alla firma' })
    if (!ok) return
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      await seguiJob(await contractsApi.signErmes(contractId, prova), prova ? 'Prova di invio alla firma' : 'Invio alla firma OTP')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  // Stato della firma digitale letto dal portale (dati portale ERMES_FIRMA): stato, link del documento, canale.
  // firmato: firma conclusa (il portale segna "F.E.A - F.E.S. completato"): contratto firmato e log scaricati negli allegati.
  type FirmaStato = { stato?: string | null; urlFirma?: string | null; canale?: string | null; letto?: string | null; firmato?: boolean; fileSalvati?: boolean | null }
  let ermesFirma: FirmaStato | null = null
  try { ermesFirma = saved?.['ERMES_FIRMA'] ? JSON.parse(saved['ERMES_FIRMA']) as FirmaStato : null } catch { ermesFirma = null }
  async function firmaAzione(kind: 'stato' | 'annulla') {
    if (kind === 'annulla' && !await confirm({ title: `Riportare la pratica ${portalCode ?? ''} in BOZZA?`, message: 'Il contratto inviato al cliente viene ritirato: la bozza torna modificabile e andrà reinviata alla firma.', confirmLabel: 'Torna in bozza', destructive: true })) return
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      await seguiJob(kind === 'stato' ? await contractsApi.ermesSignStatus(contractId) : await contractsApi.ermesSignCancel(contractId),
        kind === 'stato' ? 'Lettura dello stato della firma' : 'Ritorno in bozza')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  // Allegati caricati dal CRM sulla bozza Ermes (dati portale ERMES_ALLEGATI): si possono rimuovere dal portale.
  let ermesAllegati: { id: string; nome: string; tipo: string }[] = []
  try { ermesAllegati = JSON.parse(saved?.['ERMES_ALLEGATI'] || '[]') as typeof ermesAllegati } catch { /* dato rovinato: nessun elenco */ }
  const [removingId, setRemovingId] = useState<string | null>(null)
  async function removeErmesAttachment(a: { id: string; nome: string }) {
    if (!await confirm({ title: 'Rimuovere l\'allegato dalla bozza Ermes?', message: `"${a.nome}" viene cancellato dalla bozza sul portale (resta nei documenti del CRM).`, confirmLabel: 'Rimuovi', destructive: true })) return
    setRemovingId(a.id)
    setError(null)
    try {
      await contractsApi.deleteErmesAttachment(contractId, a.id)
      setSaved(await contractsApi.getPortalData(contractId).then(d => d ?? {}))
      setUploadMsg(`Allegato "${a.nome}" rimosso dalla bozza Ermes.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRemovingId(null)
    }
  }

  async function uploadToPortal() {
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      setUploadMsg((await contractsApi.uploadTeamwork(contractId)).message)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  // «Carica sul portale» (Coraly · Sky, 31/10/2026): il CRM crea la card con i dati per il portale e il prodotto, la collega e la
  // pratica passa a «Bozza operatore creata» senza passare dal backoffice Equal. Lo può fare anche l'agente della pratica.
  async function uploadToCoraly() {
    if (!await confirm({ title: 'Caricare la pratica sul portale?', message: 'Il CRM crea la card sul portale con i dati per il portale e il prodotto della pratica e la collega qui: la pratica passa a «Bozza operatore creata» e da lì segue le fasi del portale (verifica dei documenti, firma digitale, inserimento), senza passare dal backoffice Equal. Controlla prima che i dati siano completi.', confirmLabel: 'Carica sul portale' })) return
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      await seguiJob(await contractsApi.coralyCreate(contractId), 'Caricamento sul portale')
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  async function uploadToErmes() {
    if (!await confirm({ title: 'Creare la bozza su Ermes?', message: "Si crea la bozza su ContrattiSmart con i dati della pratica. Il controllo e l'invio alla firma restano da fare sul portale.", confirmLabel: 'Crea bozza' })) return
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      await seguiJob(await contractsApi.uploadErmes(contractId), 'Creazione della bozza su Ermes')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  // Bozza già creata e dati corretti nel CRM: si ricompilano le pagine della bozza sul portale (allegati esclusi).
  // Con `allegati` si caricano anche bolletta e documenti del CRM, convertiti in immagini (quelli con lo stesso nome già
  // sul portale si saltano).
  async function updateErmes(allegati = false) {
    const ok = allegati
      ? await confirm({ title: `Caricare gli allegati sulla bozza ${portalCode ?? ''}?`, message: 'Bolletta, documenti e moduli firmati dal CRM (Allegato A, Porta un amico) vengono caricati su Ermes come immagini (i PDF si convertono pagina per pagina) nelle caselle bolletta, documenti ed extra. I file con lo stesso nome già presenti sul portale non si ricaricano; il modulo Porta un amico viene rifatto. Anche i dati della bozza vengono ricompilati.', confirmLabel: 'Carica allegati' })
      : await confirm({ title: `Aggiornare la bozza ${portalCode ?? ''} su Ermes?`, message: 'Le pagine della bozza vengono ricompilate con i dati della pratica e si aggiungono le operazioni sul sito mancanti. Gli allegati già caricati restano. Funziona solo finché la bozza è modificabile sul portale.', confirmLabel: 'Aggiorna bozza' })
    if (!ok) return
    setUploading(true)
    setError(null)
    setUploadMsg(null)
    try {
      await seguiJob(await contractsApi.updateErmes(contractId, allegati), allegati ? 'Caricamento degli allegati su Ermes' : 'Aggiornamento della bozza su Ermes')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
    }
  }

  useEffect(() => {
    let alive = true
    contractsApi.getPortalData(contractId)
      .then(d => { if (alive) setSaved(d ?? {}) })
      .catch(err => { if (alive) { setSaved({}); setError(err instanceof Error ? err.message : String(err)) } })
    return () => { alive = false }
  }, [contractId, reloadKey])

  const config = operatorPortalConfig(operatorName, operatorId)
  const isErmes = operatorId === 18 || /ermes/i.test(operatorName ?? '')
  // Portali Coraly su cui il CRM sa creare la card (oggi Sky): per gli altri la card la crea ancora il backoffice a mano.
  const isCoralyUpload = coralyUploadFromCrm(config.profile?.portal)
  // Collegamento Coraly sospeso dal server (1/11/2026, portale passato alla nuova piattaforma): avviso al posto del pulsante.
  const [coralySuspended, setCoralySuspended] = useState<string | null>(null)
  useEffect(() => {
    if (!isCoralyUpload) { setCoralySuspended(null); return }
    let alive = true
    contractsApi.coraly(contractId)
      .then(s => { if (alive) setCoralySuspended(s.suspended ? (s.suspendedMessage || 'Collegamento con il portale sospeso.') : null) })
      .catch(() => { if (alive) setCoralySuspended(null) })
    return () => { alive = false }
  }, [contractId, isCoralyUpload, reloadKey])

  // Tipi dei documenti allegati: PDA firmata (Teamwork) e bolletta (fattura obbligatoria su Ermes).
  const [docTypes, setDocTypes] = useState<string[] | null>(null)
  useEffect(() => {
    if (!config.signedPda && !config.ermes) return
    let alive = true
    documentsApi.list('Contract', contractId)
      .then(list => {
        if (!alive) return
        setDocTypes(list.map(x => x.documentType ?? ''))
        const d = list.find(x => x.documentType === SIGNED_PDA_DOCUMENT_TYPE)
        setPda(d ? { id: d.id, file: new File([], d.name) } : null)
      })
      .catch(() => { /* la PDA si può comunque caricare: il backend controlla all'invio */ })
    return () => { alive = false }
  }, [contractId, config.signedPda, config.ermes, reloadKey])

  async function onPdaFile(file: File | null) {
    setPdaBusy(true)
    setError(null)
    try {
      if (file) {
        const { id } = await documentsApi.upload('Contract', contractId, file, undefined, undefined, SIGNED_PDA_DOCUMENT_TYPE)
        setPda({ id, file })
      } else if (pda) {
        await documentsApi.remove(pda.id)
        setPda(null)
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setPdaBusy(false)
    }
  }

  const tipo = tipoClienteFromSegment(customerSegment ?? '') || (saved?.['TIPO_CLIENTE'] ?? '')
  const values = draft ?? saved ?? {}
  // I moduli firmati si caricano tra gli allegati: qui non contano come mancanti.
  const missing = missingPortalFields(values, config, tipo, pda ? { [SIGNED_PDA_KEY]: pda.file } : {},
    config.ermes && docTypes !== null ? docTypes.includes('bolletta') : undefined)
    .filter(m => !m.startsWith('Modulo'))

  // Un tocco su Annulla (accanto a Salva) non deve buttare via quello che si è appena scritto.
  async function cancelDraft() {
    const dirty = draft !== null && saved !== null &&
      Object.keys({ ...saved, ...draft }).some(k => (draft[k] ?? '').trim() !== (saved[k] ?? '').trim())
    if (dirty && !await confirm({ title: 'Annullare le modifiche?', message: 'I dati scritti e non salvati andranno persi.',
      confirmLabel: 'Annulla modifiche', cancelLabel: 'Continua a modificare', destructive: true })) return
    setDraft(null)
  }

  async function save() {
    if (!draft) return
    setSaving(true)
    setError(null)
    try {
      const clean: Record<string, string> = {}
      for (const [k, v] of Object.entries({ ...withPortalDefaults(draft, config), TIPO_CLIENTE: tipo })) {
        const t = (v ?? '').trim()
        if (t) clean[k] = t
      }
      // Ermes: l'opzione tariffaria del portale è residente/non residente; il vecchio valore generico ("BT") non va mandato.
      if (config.ermes && clean['RESIDENTE_POD']) delete clean['POD_OPZ_TARIFFARIA']
      await contractsApi.setPortalData(contractId, clean)
      setSaved(clean)
      setDraft(null)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader
        icon="badge"
        title="Dati per il portale"
        subtitle={readOnly ? 'Modificabili solo con la pratica in Bozza o Integrazione richiesta' : undefined}
        actions={<>
          {canUploadPortal && config.signedPda && draft === null && (
            <Button variant="text" onClick={() => void uploadToPortal()} disabled={uploading}>
              {uploading ? 'Invio...' : 'Carica su Teamwork'}
            </Button>
          )}
          {canUploadPortal && isErmes && draft === null && (
            <Button variant="text" onClick={() => void uploadToErmes()} disabled={uploading}>
              {uploading ? 'Invio...' : 'Carica su Ermes'}
            </Button>
          )}
          {canUploadPortal && isCoralyUpload && !coralySuspended && draft === null && (
            <Button variant="text" onClick={() => void uploadToCoraly()} disabled={uploading}>
              {uploading ? 'Invio...' : 'Carica sul portale'}
            </Button>
          )}
          {!readOnly && saved !== null && (
            <Button variant="text" onClick={() => void rereadDocuments()} disabled={rereading || saving}>
              {rereading ? 'Lettura...' : 'Rileggi dai documenti'}
            </Button>
          )}
          {readOnly || saved === null ? null : draft === null
            ? <Button variant="text" onClick={entraInModifica}>Modifica dati</Button>
            : <>
                <Button variant="text" onClick={() => void cancelDraft()} disabled={saving}>Annulla</Button>
                <Button variant="text" onClick={() => void save()} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva'}</Button>
              </>}
        </>}
      />
      <CardContent>
        {draft !== null && error && <Callout tone="error">{error}</Callout>}
        {draft !== null && uploadMsg && <Callout tone="info">{uploadMsg}</Callout>}
        {saved === null ? <LoadingState label="Caricamento..." /> : <>
          {status !== undefined && operatorId != null && (
            <PortalUploadGuide config={config} isErmes={isErmes} missing={missing} pdaLoaded={pda !== null}
              status={status} portalCode={portalCode ?? null} coralySuspended={coralySuspended} />
          )}
          {isCoralyUpload && coralySuspended && <Callout tone="warning">{coralySuspended}</Callout>}
          {missing.length > 0 && <Callout tone="warning">Mancano: {missing.join(', ')}</Callout>}
          {/* Comando di caricamento ben visibile (lo stesso dell'intestazione): per Ermes crea la bozza ContrattiSmart. */}
          {canUploadPortal && isErmes && draft === null && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-3 mb-3">
              <Button variant="filled" icon="cloud_upload" loading={uploading} disabled={uploading} onClick={() => void uploadToErmes()}>
                {saved?.['ERMES_BOZZA_INCOMPLETA'] ? 'Riprendi la bozza su Ermes' : 'Carica su Ermes'}
              </Button>
              <span className="md-typescale-body-small text-muted">
                {/* Bozza rimasta a metà sul portale (dati portale ERMES_BOZZA_INCOMPLETA): si riprende quella, non se ne crea un'altra. */}
                {saved?.['ERMES_BOZZA_INCOMPLETA']
                  ? `Sul portale è rimasta la bozza incompleta id ${saved['ERMES_BOZZA_INCOMPLETA']}: il caricamento la riprende e la completa con questi dati e gli allegati, senza crearne un'altra. «Aggiorna la bozza» compare solo quando la bozza è completa e ha il suo numero.`
                  : 'Crea la bozza su ContrattiSmart (ebox) con questi dati; il numero della bozza arriva nelle note della pratica.'}
                {missing.length > 0 && ' Con dati mancanti il portale potrebbe rifiutarla.'}
              </span>
            </div>
          )}
          {/* Coraly (Sky): la card la crea il CRM; il codice arriva qui e la pratica segue le fasi del portale (scheda «Portale»). */}
          {canUploadPortal && isCoralyUpload && !coralySuspended && draft === null && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-3 mb-3">
              <Button variant="filled" icon="cloud_upload" loading={uploading} disabled={uploading} onClick={() => void uploadToCoraly()}>
                Carica sul portale
              </Button>
              <span className="md-typescale-body-small text-muted">
                Crea la card sul portale con questi dati e il prodotto della pratica, la collega e porta la pratica a «Bozza operatore creata»: da lì stato e messaggi arrivano dal portale.
                {missing.length > 0 && ' Con dati mancanti il portale potrebbe rifiutarla o sospenderla.'}
              </span>
            </div>
          )}
          {/* A contratto firmato la bozza non è più modificabile: restano solo il pannello della firma e gli allegati. */}
          {canUpdateErmes && isErmes && draft === null && !ermesFirma?.firmato && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-3 mb-3">
              <Button variant="tonal" icon="sync" loading={uploading} disabled={uploading} onClick={() => void updateErmes()}>
                Aggiorna la bozza su Ermes
              </Button>
              <Button variant="outlined" icon="image" loading={uploading} disabled={uploading} onClick={() => void updateErmes(true)}>
                Carica gli allegati su Ermes
              </Button>
              <Button variant="filled" icon="draw" loading={uploading} disabled={uploading} onClick={() => void signErmes()}>
                Invia alla firma OTP
              </Button>
              <Button variant="text" size="sm" loading={uploading} disabled={uploading} onClick={() => void signErmes(true)}>
                Prova invio (senza firma)
              </Button>
              <HelpTip>
                Bozza {portalCode}: «Aggiorna» rimanda i dati corretti qui e le operazioni sul sito mancanti; «Carica gli allegati» manda anche bolletta, documenti e moduli firmati dal CRM (Allegato A, Porta un amico) come immagini (quelli già sul portale con lo stesso nome si saltano); «Invia alla firma OTP» ricompila e fa partire la firma digitale del cliente.
              </HelpTip>
            </div>
          )}
          {/* Pannello della firma: sempre presente con un codice portale, anche senza stato salvato (bozze inviate alla firma
              dal portale o prima di questa funzione): «Leggi lo stato» lo recupera dai Dettagli della pratica su Ermes. */}
          {isErmes && canUpdateErmes && draft === null && (
            <Callout tone={ermesFirma?.firmato ? 'success' : 'info'} className="mb-3" title={ermesFirma?.firmato ? 'Firma digitale Ermes: contratto firmato' : `Firma digitale Ermes: ${ermesFirma?.stato ?? 'stato non ancora letto dal portale'}`}>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {ermesFirma && !ermesFirma.firmato && ermesFirma.urlFirma && (
                  <a href={ermesFirma.urlFirma} target="_blank" rel="noopener noreferrer">
                    <Button variant="filled" icon="open_in_new">Apri il documento per la firma</Button>
                  </a>
                )}
                {ermesFirma?.firmato && ermesFirma.fileSalvati !== false && (
                  <a href="#contract-allegati"><Button variant="filled" icon="attach_file">Vai agli allegati</Button></a>
                )}
                <Button variant={ermesFirma ? 'text' : 'tonal'} size={ermesFirma ? 'sm' : undefined} icon={ermesFirma ? undefined : 'refresh'} loading={uploading} disabled={uploading} onClick={() => void firmaAzione('stato')}>
                  {ermesFirma ? 'Aggiorna stato' : 'Leggi lo stato della firma'}
                </Button>
                {ermesFirma && !ermesFirma.firmato && (
                  <Button variant="text" size="sm" tone="error" disabled={uploading} onClick={() => void firmaAzione('annulla')}>Torna allo stato BOZZA</Button>
                )}
              </div>
              <span className="md-typescale-body-small">
                {!ermesFirma
                  ? <HelpTip>{'Legge dai Dettagli della pratica su Ermes se è in bozza, inviata alla firma o già firmata: con la firma conclusa salva qui contratto firmato e log della firma. L\'esito arriva entro un minuto (la pagina si aggiorna da sola).'}</HelpTip>
                  : ermesFirma.firmato && ermesFirma.fileSalvati === false
                  ? `Il portale segna «${ermesFirma.stato ?? 'documento firmato'}» ma non offre ancora il contratto firmato e il log (elaborazione dell'esito in corso): il CRM riprova da solo ogni 10 minuti e li salva negli allegati appena compaiono («Aggiorna stato» per riprovare subito).`
                  : ermesFirma.firmato
                  ? `Il portale segna «${ermesFirma.stato ?? 'documento firmato'}»: il contratto firmato e il log della firma sono stati salvati fra gli allegati della pratica (ricaricare la pagina se non compaiono).`
                  : <>{ermesFirma.canale ? `Inviato via ${ermesFirma.canale}. ` : 'In attesa della firma del cliente. '}Il CRM controlla Ermes da solo ogni 10 minuti.<HelpTip>{`La firma avviene sulla pagina del documento (servizio esterno Namirial, la stessa del link ricevuto via SMS), non nel CRM: lì il cliente, o l'agente con lui, preme «Firma», riceve il codice OTP sul cellulare, lo inserisce e conferma con «COMPLETA». A firma fatta il CRM se ne accorge da solo entro 10 minuti: la pratica passa a «In lavorazione operatore», contratto firmato e log della firma finiscono negli allegati e l'agente riceve una notifica. «Aggiorna stato» serve solo a non aspettare.`}</HelpTip></>}
                {ermesFirma?.letto ? ` Letto il ${new Date(ermesFirma.letto).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })}.` : ''}
              </span>
            </Callout>
          )}
          {isErmes && ermesErrori.length > 0 && (
            <Callout tone="error" className="mb-3" title="Ermes non ha accettato l'invio alla firma: da sistemare">
              <ul className="m-0 ps-3">
                {ermesErrori.map((e, i) => {
                  // Allegato mancante in una casella del portale: si risolve dagli Allegati della pratica, con il tipo giusto.
                  const allegato = /allegato di tipo\s+([\w ']+)/i.exec(e.messaggio)?.[1]?.trim()
                  return (
                    <li key={i} className="md-typescale-body-small">
                      {e.campo ? <strong>{portalFieldLabel(e.campo)}</strong> : null}{e.campo ? ': ' : ''}{e.messaggio}{e.passo ? ` (passo ${e.passo} del portale)` : ''}
                      {e.campo && !readOnly && (
                        <Button variant="text" size="sm" onClick={() => correggi(e.campo)}>Correggi</Button>
                      )}
                      {allegato && (
                        <>
                          {' '}<span className="text-muted">Negli Allegati della pratica carica (o segna con il tipo) un file «{allegato}», poi «Carica gli allegati su Ermes» e ripeti l'invio.</span>
                          <Button variant="text" size="sm" onClick={() => document.getElementById('contract-allegati')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Vai agli allegati</Button>
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
              <span className="md-typescale-body-small">Correggi i dati, premi Salva e poi di nuovo «Invia alla firma OTP». Un errore senza campo né allegato va risolto sul portale.</span>
            </Callout>
          )}
          {canUploadPortal && config.signedPda && !isErmes && draft === null && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-3 mb-3">
              <Button variant="filled" icon="cloud_upload" loading={uploading} disabled={uploading} onClick={() => void uploadToPortal()}>
                Carica su Teamwork
              </Button>
              <span className="md-typescale-body-small text-muted">L'esito e il codice Teamwork arrivano nelle note della pratica.</span>
            </div>
          )}
          {isErmes && draft === null && ermesAllegati.length > 0 && (
            <div className="mb-3">
              <span className="d-block md-typescale-body-small text-muted mb-1">Allegati caricati dal CRM sulla bozza Ermes</span>
              <ul className="m-0 ps-3">
                {ermesAllegati.map(a => (
                  <li key={a.id} className="md-typescale-body-small">
                    {a.nome} <span className="text-muted">({a.tipo === 'bolletta' ? 'bolletta' : a.tipo === 'documenti' ? 'documento di identità' : 'documenti aggiuntivi'})</span>
                    {canUpdateErmes && (
                      <Button variant="text" size="sm" tone="error" disabled={removingId !== null} loading={removingId === a.id} onClick={() => void removeErmesAttachment(a)}>
                        Rimuovi dal portale
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Esito del caricamento accanto ai pulsanti (in cima alla scheda sarebbe fuori dallo schermo). */}
          {draft === null && error && <Callout tone="error" className="mb-3">{error}</Callout>}
          {draft === null && jobRun && (
            <Callout tone="info" className="mb-3" icon="hourglass_top">
              {jobRun.label} in corso sul portale… {jobRun.elapsed} s. Di solito serve da 30 secondi a 2 minuti: l'esito compare qui appena il portale risponde.
            </Callout>
          )}
          {draft === null && uploadMsg && <Callout tone="info" className="mb-3">{uploadMsg}</Callout>}
          {draft !== null && customerId != null && config.kind !== 'telco' && (
            <FornitureNoteSelect customerId={customerId} excludeContractId={contractId} disabled={saving}
              onApply={fields => setDraft(d => ({ ...(d ?? saved), ...fields }))} />
          )}
          <PraticaPortalSections
            values={values}
            onChange={(k, v) => setDraft(d => ({ ...(d ?? saved), [k]: v }))}
            disabled={draft === null || saving}
            config={config}
            tipo={tipo}
            // La PDA firmata si carica in qualsiasi stato (serve al portale); toglierla solo finché la pratica è modificabile.
            filesDisabled={pdaBusy || (readOnly && pda !== null)}
            moduleFiles={pda ? { [SIGNED_PDA_KEY]: pda.file } : {}}
            onModuleFile={(flag, file) => { if (flag === SIGNED_PDA_KEY) void onPdaFile(file) }}
          />
          {/* Su telefono i pulsanti in alto restano lontani dai campi: Salva anche in fondo, ben visibile. */}
          {draft !== null && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="text" onClick={() => void cancelDraft()} disabled={saving}>Annulla</Button>
              <Button variant="filled" onClick={() => void save()} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva dati'}</Button>
            </div>
          )}
        </>}
      </CardContent>
    </Card>
  )
}
